import React, { useEffect, useRef } from "react";
import { v4 as uuidv4 } from "uuid";
import useTitanApp from "@/hooks/useTitanApp";
import {
  setOpenPrescriptionDialog,
  setIncrementPrescriptionCompleted,
  setDynamicPrescriptions,
  setPrescriptionId,
  setIsPrescriptionUpdate,
  setSelectedPrescriptionCategory,
} from "@/dispatchers/patientDispatchers";

interface PrescriptionPrefillItem {
  medication: string;
  dosage: string;
  quantity: string;
  notes?: string;
  prescribedItemId?: string;
}

interface PrescriptionModalProps {
  trigger?: React.ReactNode;
  isDarkMode?: boolean;
  prefillItems?: PrescriptionPrefillItem[];
  allowedMedications?: any[];
  defaultCategory?: "pgd" | "pharmacy-first";
  lockedMedications?: boolean;
  onSave?: (prescription: any) => void;
}

/**
 * Bridges PathwayEngine's trigger+onSave interface to the existing
 * prescription modal that lives in PatientQuickActions (controlled
 * via Redux openPrescriptionDialog state).
 */
export function PrescriptionModal({
  trigger,
  prefillItems,
  defaultCategory,
  onSave,
}: PrescriptionModalProps) {
  const { patientState } = useTitanApp();
  const { prescriptionCompleted, dynamicPrescriptions } = patientState || {};

  const waitingForCompletion = useRef(false);

  useEffect(() => {
    if (waitingForCompletion.current && prescriptionCompleted) {
      waitingForCompletion.current = false;
      onSave?.({ items: dynamicPrescriptions || [] });
    }
  }, [prescriptionCompleted]);

  const handleClick = () => {
    setIncrementPrescriptionCompleted(false);
    waitingForCompletion.current = true;

    setPrescriptionId(null);
    setIsPrescriptionUpdate(false);
    setSelectedPrescriptionCategory(defaultCategory || "private");

    if (prefillItems && prefillItems.length > 0) {
      // `id` must be present: the prescription modal keys its per-item
      // validation errors (and its remove-item filter) off item.id. Without it
      // every prefilled row collapses onto a single `undefined` key, so the
      // "Medication/Dosage/Quantity is required" messages never render against
      // the field that is actually empty.
      const mappedItems = prefillItems.map((item) => ({
        id: uuidv4(),
        value: item.prescribedItemId ? item.medication : "",
        dose: item.dosage || "",
        // Deliberately left blank. The modal's quantity field is a number
        // input and the PGD's quantity is a descriptive label ("7 tablets
        // (week 1)"), which a number input renders as blank while still
        // holding a truthy string — the field would look empty yet pass the
        // required validation, and Number(label) would reach the API as NaN.
        // Leaving it empty makes the pharmacist enter a real number and lets
        // the "Quantity is required*" validation do its job.
        quantity: "",
        notes: item.notes || "",
        prescribedItemId: item.prescribedItemId || "",
      }));
      setDynamicPrescriptions(mappedItems);
    } else {
      // Seed one blank row (same shape as the modal's own "add item") so the
      // required-field validation has something to report against. An empty
      // list would validate as valid and allow saving with no items at all.
      setDynamicPrescriptions([
        {
          id: uuidv4(),
          value: "",
          dose: "",
          quantity: "",
          notes: "",
          prescribedItemId: "",
        },
      ]);
    }

    setOpenPrescriptionDialog(true);
  };

  return (
    <div onClick={handleClick} style={{ display: "contents" }}>
      {trigger}
    </div>
  );
}
